// node_modules
import { FC, useContext, useEffect, useState } from "react";
import { faPencilAlt, faSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Components
import { MarkdownItComponent } from "Components";
import { ProseMirrorEditor } from "..";
// Controllers
import { ProseMirrorPluginControllerSingleton } from "Controllers";
// styles
import styles from "./editableMarkdown.module.scss";
// Contexts
import { WebsocketContext } from "Providers";
// Constants
import { HubConstants } from "Constants";
// Enums
import { ToastTypeEnum } from "Enums";
// Helpers
import { ToastHelperSingleton } from "Helpers";

type TEditableMarkdownProps = {
    extraClassNameWisiwygBottomContainer?: string,
    source: string,
    entityId: string,
    noSource?: string,
    isEditable: boolean,
    env?: any,
    resourceName?: string,
    handleSourceEdited?: (text: string) => void
}

export const EditableMarkdown: FC<TEditableMarkdownProps> = ({ extraClassNameWisiwygBottomContainer, entityId, source, noSource, isEditable, env, resourceName, handleSourceEdited }: TEditableMarkdownProps) => {
    // State
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editableMarkdown, setEditableMarkdown] = useState<string>(source);
    const [currentEntityId, setCurrentEntityId] = useState<string>(entityId);

    // Get websocket context
    const { webSocketController } = useContext(WebsocketContext);

    // Logic
    useEffect(() => {
        setEditableMarkdown(source);
        if (currentEntityId.trim().toLowerCase() !== entityId.trim().toLowerCase()) {
            setIsEditing(false);
            setCurrentEntityId(entityId.trim().toLowerCase());
        }
    }, [currentEntityId, entityId, source]);

    const updateEditableMarkdown = (text: string) => {
        setEditableMarkdown(text);
        if(handleSourceEdited) handleSourceEdited(text);
    };

    const receivedEditStatusChange = (newIsEditingValue: boolean) => {
        setIsEditing(newIsEditingValue);
    };

    const receivedAlreadyBeingEdited = () => {
        ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Somebody is already editing this field.");
    };

    useEffect(() => {
        if(resourceName) {
            webSocketController.addHandler(`${HubConstants.DELIVERY_RESOURCE_EDIT_STATUS_CHANGE_PREFIX}${resourceName}`, receivedEditStatusChange);
            webSocketController.addHandler(`${HubConstants.DELIVERY_RESOURCE_ALREADY_BEING_EDITED_PREFIX}${resourceName}`, receivedAlreadyBeingEdited);

            // on clean up time, send stop editing message to prevent claiming lock 
            // and remove handlers
            return () => {
                webSocketController.invokeFunction(HubConstants.STOP_EDITING_DELIVERY_RESOURCE, resourceName);
                webSocketController.removeHandler(`${HubConstants.DELIVERY_RESOURCE_EDIT_STATUS_CHANGE_PREFIX}${resourceName}`, receivedEditStatusChange);
                webSocketController.removeHandler(`${HubConstants.DELIVERY_RESOURCE_ALREADY_BEING_EDITED_PREFIX}${resourceName}`, receivedAlreadyBeingEdited);
            };
        }
    }, [resourceName, webSocketController]);

    const changeIsEditing = (doStartEditing: boolean) => {
        if(resourceName) {
            if(doStartEditing) {
                webSocketController.invokeFunction(HubConstants.START_EDITING_DELIVERY_RESOURCE, resourceName);
            } else {
                webSocketController.invokeFunction(HubConstants.STOP_EDITING_DELIVERY_RESOURCE, resourceName);
            }
        } else {
            setIsEditing(doStartEditing);
        }
    };

    const handleOnDoubleClick = (): void => {
        if (!isEditable) { return; }
        changeIsEditing(true);
    };
    
    return (
        <>
            {isEditable && isEditing ? 
                <div className={[styles.editableMarkdownContainer, styles.isInEditMode].join(" ")}>
                    <button className={[styles.changeContentButton, styles.save].join(" ")} title={"Exit edit mode"} onClick={() => { changeIsEditing(false); } }>
                        <FontAwesomeIcon icon={faPencilAlt} />
                        <FontAwesomeIcon className={styles.slash} icon={faSlash} />
                        <FontAwesomeIcon className={styles.slashBottom} icon={faSlash} />
                    </button>
                    <ProseMirrorEditor source={editableMarkdown} plugins={ProseMirrorPluginControllerSingleton.getDefault()} 
                        documentChanged={updateEditableMarkdown} extraClassNameWisiwygBottomContainer={extraClassNameWisiwygBottomContainer} />
                </div>
            : 
                <div className={styles.editableMarkdownContainer}>
                    {isEditable && <button className={[styles.changeContentButton, styles.edit].join(" ")} title={"Edit mode"} onClick={() => { changeIsEditing(true); } }>
                        <FontAwesomeIcon icon={faPencilAlt} />
                    </button> }
                    <MarkdownItComponent handleOnDoubleClick={handleOnDoubleClick} source={editableMarkdown} noSource={noSource} env={env}/>
                </div>
            }
        </>
    );
};